import { useState, Fragment } from "react";
import { useStore } from "zustand";
import PropTypes from "prop-types";
import {
  Heading,
  ActionButton,
  Divider,
  Text,
  Link,
  Avatar,
  LinkButton,
} from "@react-spectrum/s2";
import EditIcon from "@react-spectrum/s2/icons/Edit";

import { store } from "@app/store";
import { PreviewPasswordPopover } from "@app/components/PreviewPasswordPopover";
import {
  GRID_TYPE,
  getCellLink,
  getGroupGrids,
  getPreviewLinks,
  isTextCell,
} from "./wizardSteps";
import { layoutGridRows } from "./campaignGridLayout";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };

const formatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

const EditButton = ({ value }) => {
  const colorScheme = useStore(store, (state) => state.colorScheme);

  const setPromptHint = useStore(store, (state) => state.setPromptHint);

  return (
    <ActionButton
      aria-label="Edit"
      isQuiet
      size="XS"
      styles={style({
        marginStart: 8,
      })}
      UNSAFE_style={{
        display: "inline-grid",
        verticalAlign: "text-bottom",
      }}
      onPress={() => {
        setPromptHint(
          `Edit ${formatter.format((Array.isArray(value) ? value : [value]).map((v) => `"${v}"`))}: `,
        );
      }}
    >
      <EditIcon
        UNSAFE_className={style({
          color: {
            colorScheme: {
              dark: "white/25",
              light: "black/25",
            },
          },
        })({ colorScheme })}
      />
    </ActionButton>
  );
};

EditButton.propTypes = {
  value: PropTypes.string.isRequired,
  setPromptHint: PropTypes.func.isRequired,
};

export const CampaignField = ({
  selectedCampaign = {},
  selectedPersona = {},
  label,
  value,
  size = "L",
  isDisabled,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div
      className={style({
        marginY: {
          size: {
            S: 4,
            M: 4,
            L: 12,
            XL: 16,
            XXL: 16,
            XXXL: 16,
          },
        },
      })({ size })}
    >
      {!!label && (
        <div
          className={style({
            // font: "detail-lg",
            font: {
              size: {
                S: "detail-sm",
                M: "detail-sm",
                L: "detail",
                XL: "detail-lg",
                XXL: "detail-lg",
                XXXL: "detail-lg",
              },
            },
          })({ size })}
        >
          {label}
        </div>
      )}
      {!isEditing && (
        <div
          className={style({
            display: "inline",
            alignItems: "center",
            font: {
              size: {
                S: "detail",
                M: "detail-lg",
                L: "body-lg",
                XL: "body-xl",
                XXL: "body-2xl",
                XXXL: "body-3xl",
              },
            },
            // whiteSpace: "nowrap",
          })({ size })}
        >
          {(Array.isArray(value) ? value : [value]).map((val, idx) => {
            if ((selectedCampaign?.[val] ?? "")?.startsWith("http")) {
              return (
                <Link
                  key={`${val}-${idx}`}
                  variant="primary"
                  href={selectedCampaign?.[val] ?? ""}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {eval(`selectedCampaign["${val}"] ?? ""`)}
                </Link>
              );
            }

            return (
              <Fragment key={`${val}-${idx}`}>
                {val === "Persona-1-name" && !!selectedPersona?.avatarUrl && (
                  <Avatar
                    src={selectedPersona?.avatarUrl}
                    alt="Persona avatar"
                    size={80}
                    isOverBackground
                    styles={style({
                      marginBottom: 12,
                    })}
                  />
                )}
                <Text>{(selectedCampaign?.[val] ?? "") + " "}</Text>
              </Fragment>
            );
          })}
          {!isDisabled && <EditButton value={value} />}
        </div>
      )}
      {/* {isEditing && (
        <div
          className={style({
            display: "flex",
            gap: 8,
          })}
        >
          <TextField autoFocus />
          <ActionButtonGroup size="XS" isQuiet>
            <ActionButton>Save</ActionButton>
            <ActionButton>Cancel</ActionButton>
          </ActionButtonGroup>
        </div>
      )} */}
    </div>
  );
};

CampaignField.propTypes = {
  selectedCampaign: PropTypes.object,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  size: PropTypes.oneOf(["S", "M", "L", "XL", "XXL", "XXXL"]),
  isDisabled: PropTypes.bool,
};

/** A campaign structure group's title and description. */
export const CampaignGroupHeader = ({ title, description }) => {
  if (!title && !description) {
    return null;
  }

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        gap: 8,
      })}
    >
      <Heading
        level={2}
        styles={style({
          font: "heading-lg",
          // color: "detail",
          // textAlign: "center",
          margin: 0,
        })}
      >
        {title}
      </Heading>
      {!!description && (
        <div className={style({ font: "detail-lg" })}>{description}</div>
      )}
    </div>
  );
};

CampaignGroupHeader.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
};

/**
 * A grid's preview links — `Preview [link] [link]`, `links` being `{ label,
 * href }` (see `getPreviewLinks`). With a `password`, the section shows it in
 * a popover (see `PreviewPasswordPopover`).
 */
export const CampaignPreviewLinks = ({ links = [], password }) => {
  if (!links.length) {
    return null;
  }

  return (
    <PreviewPasswordPopover
      password={password}
      styles={style({
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      })}
    >
      <span className={style({ font: "detail", color: "neutral-subdued" })}>
        Preview
      </span>
      {links.map(({ href, label, target, rel }, idx) => (
        <LinkButton
          key={`${href}-${idx}`}
          href={href}
          target={target}
          rel={rel}
          variant="secondary"
          staticColor="auto"
          size="S"
        >
          {label}
        </LinkButton>
      ))}
    </PreviewPasswordPopover>
  );
};

CampaignPreviewLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      target: PropTypes.string,
      rel: PropTypes.string,
    }),
  ),
  password: PropTypes.string,
};

/**
 * A campaign structure grid's optional title and description, with its
 * `previewLinks` and then `actions` beside the title. The preview links'
 * `previewPassword` is shown on hover.
 */
export const CampaignGridHeader = ({
  title,
  description,
  previewLinks,
  previewPassword,
  actions,
}) => {
  const hasPreviewLinks = !!previewLinks?.length;

  if (!title && !description && !actions && !hasPreviewLinks) {
    return null;
  }

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        gap: 8,
      })}
    >
      {(!!title || !!actions || hasPreviewLinks) && (
        <div
          className={style({
            display: "flex",
            flexDirection: {
              default: "column-reverse",
              md: "row",
            },
            alignItems: "baseline",
            gap: 16,
          })}
        >
          {!!title && (
            <Heading
              level={3}
              styles={style({
                font: "title-lg",
                // color: "detail",
                margin: 0,
              })}
            >
              {title}
            </Heading>
          )}
          <CampaignPreviewLinks
            links={previewLinks}
            password={previewPassword}
          />
          {actions}
        </div>
      )}
      {!!description && (
        <div className={style({ font: "detail" })}>{description}</div>
      )}
    </div>
  );
};

CampaignGridHeader.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  previewLinks: PropTypes.array,
  previewPassword: PropTypes.string,
  actions: PropTypes.node,
};

const MAX_COLUMN_WIDTH = size(300);

// A grid's rows as one CSS grid, positioned through the CSS variables
// `CampaignFieldRows` sets — static styles can't take dynamic values. Below
// `md` it is a single column, its cells stacked in order.
// Lines are as tall as their own content (`--grid-rows`, see `getGridRows`).
const GRID_STYLES = {
  none: style({
    display: "grid",
    gridTemplateColumns: {
      default: "minmax(0, 1fr)",
      md: "repeat(var(--grid-columns), minmax(0, 1fr))",
    },
    gridTemplateRows: { default: "none", md: "var(--grid-rows, none)" },
    alignItems: "start",
    columnGap: 20,
    rowGap: 0,
  }),
  variations: style({
    display: "grid",
    gridTemplateColumns: {
      default: "minmax(0, 1fr)",
      md: "repeat(var(--grid-columns), minmax(0, 1fr))",
    },
    gridTemplateRows: { default: "none", md: "var(--grid-rows, none)" },
    alignItems: "start",
    columnGap: 20,
    rowGap: 24,
  }),
  // The tracks of the cell it fills — itself a subgrid (see `isSubgrid`).
  subgrid: style({
    display: "grid",
    gridTemplateColumns: { default: "minmax(0, 1fr)", md: "subgrid" },
    gridTemplateRows: { default: "none", md: "var(--grid-rows, none)" },
    gridColumnStart: "1",
    gridColumnEnd: "-1",
    alignItems: "start",
    columnGap: 20,
    rowGap: 0,
  }),
};

const GRID_CELL_STYLE = style({
  minWidth: 0,
  gridColumnStart: { default: "auto", md: "var(--cell-column)" },
  gridColumnEnd: { default: "auto", md: "span var(--cell-col-span)" },
  gridRowStart: { default: "auto", md: "var(--cell-line)" },
  gridRowEnd: { default: "auto", md: "span var(--cell-row-span)" },
});

// A cell passing the tracks it spans on to its content, a subgrid in turn.
const GRID_SUBGRID_CELL_STYLE = style({
  minWidth: 0,
  gridColumnStart: { default: "auto", md: "var(--cell-column)" },
  gridColumnEnd: { default: "auto", md: "span var(--cell-col-span)" },
  gridRowStart: { default: "auto", md: "var(--cell-line)" },
  gridRowEnd: { default: "auto", md: "span var(--cell-row-span)" },
  display: "grid",
  gridTemplateColumns: { default: "minmax(0, 1fr)", md: "subgrid" },
});

/**
 * The row tracks of a laid out grid with cells spanning rows, or `undefined`
 * without any. A spanning cell would otherwise split its height equally
 * between the `auto` lines it covers, so each line would be as tall as the
 * tallest. A flexible last line takes what the spanning cell needs beyond the
 * lines above it instead, those staying as tall as their own content.
 */
const getGridRows = ({ lines, cells }) => {
  const spanEnds = new Set(
    cells.filter((c) => c.rowSpan > 1).map((c) => c.line + c.rowSpan - 1),
  );

  return spanEnds.size
    ? Array.from({ length: lines }, (_, idx) =>
        spanEnds.has(idx + 1) ? "minmax(auto, 1fr)" : "auto",
      ).join(" ")
    : undefined;
};

/**
 * The rows of a campaign structure grid, laid out together as one grid (see
 * `layoutGridRows`). Every row splits its width equally between its columns —
 * one per field, or `colSpan` of them for a field that spans several — so a
 * lone field spans the whole row. A field may span the rows below too
 * (`rowSpan`), which then lay their own fields out around it. Everything
 * stacks into a single column on narrow screens.
 *
 * `columns` fixes the column count of every row instead, so a row with fewer
 * fields leaves its trailing columns empty. A `null` field is an empty column.
 * A row has at most `MAX_ROW_COLUMNS` columns; fields that do not fit wrap
 * onto a new line. `rowGap` spaces the lines: `"none"` for fields, which keep
 * their own margins, or `"variations"` for image cards.
 *
 * With `subgrid`, the rows are laid out on the tracks of the grid cell they
 * fill instead — `columns` of them — the last field of a line stretching over
 * what is left of it. Every element between that cell and the rows must be a
 * subgrid too: a field marked `isSubgrid` gets a cell that is, and its
 * content must follow (see `CampaignFieldBox`).
 */
export const CampaignFieldRows = ({
  rows = [],
  columns,
  rowGap = "none",
  subgrid = false,
  renderField,
}) => {
  const layout = layoutGridRows(rows, { columns, fillLines: subgrid });

  return (
    <div
      className={
        subgrid
          ? GRID_STYLES.subgrid
          : (GRID_STYLES[rowGap] ?? GRID_STYLES.none)
      }
      style={{
        "--grid-columns": subgrid ? undefined : layout.columns,
        "--grid-rows": getGridRows(layout),
      }}
    >
      {layout.cells.map(
        ({ cell, rowIdx, cellIdx, column, colSpan, line, rowSpan }) => (
          <div
            key={`${rowIdx}-${cellIdx}`}
            className={
              cell?.isSubgrid ? GRID_SUBGRID_CELL_STYLE : GRID_CELL_STYLE
            }
            style={{
              "--cell-column": column,
              "--cell-col-span": colSpan,
              "--cell-line": line,
              "--cell-row-span": rowSpan,
            }}
          >
            {cell ? renderField(cell, cellIdx) : null}
          </div>
        ),
      )}
    </div>
  );
};

CampaignFieldRows.propTypes = {
  columns: PropTypes.number,
  rowGap: PropTypes.oneOf(["none", "variations"]),
  subgrid: PropTypes.bool,
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)),
  renderField: PropTypes.func.isRequired,
};

export const CampaignPreview = ({
  selectedCampaign = {},
  selectedPersona = {},
  campaignStructure = [],
  templateData,
}) => {
  return (
    <div
      className={style({
        width: "full",
        maxWidth: 640,
        marginX: "auto",
        textWrap: "pretty",
      })}
    >
      {campaignStructure.map((group, idx) => (
        <Fragment key={idx}>
          <Divider size="S" />
          <div
            className={style({
              boxSizing: "border-box",
              width: "full",
              paddingX: 24,
              marginTop: 12,
              marginBottom: 48,
            })}
          >
            <CampaignGroupHeader
              title={group.title}
              description={group.description}
            />
            {getGroupGrids(group)
              .filter((grid) => grid.type !== GRID_TYPE.VARIATIONS)
              .map((grid, gridIdx) => (
                <Fragment key={gridIdx}>
                  <CampaignGridHeader
                    title={grid.title}
                    description={grid.description}
                    previewLinks={getPreviewLinks(
                      grid.previewLinks,
                      templateData?.previewUrl,
                    )}
                    previewPassword={templateData?.previewPassword}
                  />
                  <CampaignFieldRows
                    rows={grid.rows}
                    renderField={(cell) => {
                      const {
                        label,
                        value,
                        size = "L",
                        disabled = false,
                      } = cell;

                      // A link is shown by the visuals preview only.
                      return !isTextCell(cell) && getCellLink(cell) ? null : (
                        <CampaignField
                          selectedCampaign={selectedCampaign}
                          selectedPersona={selectedPersona}
                          label={label}
                          value={value}
                          size={size}
                          isDisabled={disabled}
                        />
                      );
                    }}
                  />
                </Fragment>
              ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
};

CampaignPreview.propTypes = {
  selectedCampaign: PropTypes.object,
  selectedPersona: PropTypes.object,
  campaignStructure: PropTypes.array,
  templateData: PropTypes.object,
};
